import {
  ROUTE_QUIZ_INTRO,
  ROUTE_QUIZ_BUDGET,
  ROUTE_QUIZ_BUDGET_BENEFITS,
  ROUTE_QUIZ_HELLO,
  ROUTE_QUIZ_GOALS,
  ROUTE_QUIZ_GOALS_BENEFITS,
  ROUTE_QUIZ_EATING_HABITS,
  ROUTE_QUIZ_EATING_HABITS_DIETARY_NEEDS,
  ROUTE_QUIZ_MACRO_NUTRIENT,
  ROUTE_QUIZ_MACRO_NUTRIENT_LOW_SODIUM,
  ROUTE_QUIZ_FOOD_QUALITIES,
  ROUTE_QUIZ_FOOD_FORMATS,
  ROUTE_QUIZ_CUISINES,
  ROUTE_QUIZ_FLAVORS,
  ROUTE_QUIZ_PROTEIN,
  ROUTE_QUIZ_POLARIZING,
  ROUTE_QUIZ_FOOD_PREFERENCES_INTRO,
  ROUTE_QUIZ_LUNCH,
  ROUTE_QUIZ_DINNER,
  ROUTE_QUIZ_SWIPE,
  ROUTE_QUIZ_MEAL_FREQUENCIES,
  ROUTE_QUIZ_DISH_TYPES,
  ROUTE_QUIZ_HEALTH_GOALS,
} from '@hungryroot/routes';
import { tags } from '@hungryroot/tags';
import { Goals, type Quiz } from './quiz';

export type ProgressSection = 'about' | 'tastes' | 'routine';

export interface QuizRouteContext {
  quiz: Quiz;
  skipQuizIntro: boolean;
  deckIsSufficient: boolean;
}

export interface QuizRouteEntry {
  path: string;
  section: ProgressSection | null;
  shouldSkip?: (ctx: QuizRouteContext) => boolean;
}

/*
  This list represents ALL possible permutations of the quiz.

  Any route that CAN be in the quiz MUST be defined here.
  Each entry declares its path, progress bar section, and optional skip condition.
  Routes with `section: null` are not shown in the progress bar (intros, interstitials).
  Routes without `shouldSkip` are always shown.

  WHEN TO ADD A NEW SEQUENCE:
  Most changes belong in `base` — add the route and use `shouldSkip` to gate it
  behind quiz data. Add a new sequence only when a flow needs to *reorder*
  existing routes, not just add or remove them. `shouldSkip` handles toggling;
  separate sequences handle ordering.
*/
export const ROUTE_SEQUENCES = {
  base: [
    {
      path: ROUTE_QUIZ_INTRO,
      section: null,
      shouldSkip: (ctx: QuizRouteContext) => ctx.skipQuizIntro,
    },
    { path: ROUTE_QUIZ_HELLO, section: 'about' },
    { path: ROUTE_QUIZ_GOALS, section: 'about' },
    {
      path: ROUTE_QUIZ_GOALS_BENEFITS,
      section: null,
      shouldSkip: (ctx: QuizRouteContext) =>
        ctx.quiz.goals === null || ctx.quiz.goals?.length === 0,
    },
    {
      path: ROUTE_QUIZ_HEALTH_GOALS,
      section: 'about',
      shouldSkip: (ctx: QuizRouteContext) =>
        !ctx.quiz.goals?.includes(Goals.ImproveHealth),
    },
    { path: ROUTE_QUIZ_BUDGET, section: 'about' },
    {
      path: ROUTE_QUIZ_BUDGET_BENEFITS,
      section: null,
      shouldSkip: (ctx: QuizRouteContext) =>
        ctx.quiz.budget === null || ctx.quiz.budget === undefined,
    },
    { path: ROUTE_QUIZ_FOOD_FORMATS, section: 'about' },
    { path: ROUTE_QUIZ_EATING_HABITS, section: 'about' },
    { path: ROUTE_QUIZ_EATING_HABITS_DIETARY_NEEDS, section: 'about' },
    { path: ROUTE_QUIZ_MACRO_NUTRIENT, section: 'about' },
    {
      path: ROUTE_QUIZ_MACRO_NUTRIENT_LOW_SODIUM,
      section: null,
      shouldSkip: (ctx: QuizRouteContext) =>
        !ctx.quiz.macroNutrient?.includes(tags.LowSodium),
    },
    { path: ROUTE_QUIZ_FOOD_QUALITIES, section: 'about' },
    { path: ROUTE_QUIZ_DISH_TYPES, section: 'about' },
    { path: ROUTE_QUIZ_CUISINES, section: 'tastes' },
    { path: ROUTE_QUIZ_FLAVORS, section: 'tastes' },
    { path: ROUTE_QUIZ_PROTEIN, section: 'tastes' },
    { path: ROUTE_QUIZ_POLARIZING, section: 'tastes' },
    {
      path: ROUTE_QUIZ_FOOD_PREFERENCES_INTRO,
      section: null,
      shouldSkip: (ctx: QuizRouteContext) => !ctx.deckIsSufficient,
    },
    // The swipe deck replaces the five meal screens (breakfast/lunch/dinner/
    // snacks/sweets). Meal frequencies below still captures how often each
    // occasion is eaten.
    {
      path: ROUTE_QUIZ_SWIPE,
      section: 'routine',
      shouldSkip: (ctx: QuizRouteContext) => !ctx.deckIsSufficient,
    },
    { path: ROUTE_QUIZ_MEAL_FREQUENCIES, section: 'routine' },
  ],
} as const satisfies Record<string, readonly QuizRouteEntry[]>;

export const LUNCH_REFRESH_ROUTES = [
  ROUTE_QUIZ_LUNCH,
  ROUTE_QUIZ_DINNER,
] as const;

const routeSequenceKeys = Object.keys(ROUTE_SEQUENCES) as Array<
  keyof typeof ROUTE_SEQUENCES
>;
export const allQuizRoutes = routeSequenceKeys.reduce((acc, key) => {
  const sequence = ROUTE_SEQUENCES[key];
  sequence.forEach((entry) => {
    if (acc.indexOf(entry.path) === -1) {
      acc.push(entry.path);
    }
  });

  return acc;
}, [] as AllQuizRoutes[]);

export type AllQuizRoutes =
  (typeof ROUTE_SEQUENCES)[keyof typeof ROUTE_SEQUENCES][number]['path'];
